import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productService, categoryService } from '../services/api';
import ExportButton from '../components/ExportButton';
import ImportButton from '../components/ImportButton';
import TemplateButton from '../components/TemplateButton';
import GoogleDriveHelp from '../components/GoogleDriveHelp';
import ProductDetailsModal from '../components/ProductDetailsModal';
import { importService } from '../services/importService';
import { normalizeImageUrl, getProductMainImage, handleImageError } from '../utils/imageUtils';
import SmartImage from '../components/SmartImage';
import './Products.css';
import { Typography, Row, Col, Card, Input, Select, Button, Table, Tag, Space, Popconfirm, Switch, Pagination, Dropdown, Modal, Tooltip, message } from 'antd';
import CustomPagination from '../components/CustomPagination';
const { Title, Text } = Typography;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: '',
    category: '',
    isVisible: ''
  });
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Synchroniser les paramètres URL avec les filtres
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    if (searchFromUrl !== filters.search) {
      setFilters(prev => ({ ...prev, search: searchFromUrl }));
      setQuery(searchFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadProducts();
    }, 250); // throttle de 250ms
    return () => {
      clearTimeout(timeout);
      // Annuler la requête en cours au démontage
      try { currentAbortController?.abort(); } catch (e) {}
    };
  }, [filters, pagination.currentPage]);

  // Charger les catégories pour le filtre
  useEffect(() => {
    (async () => {
      try {
        const res = await categoryService.getCategories({ includeInactive: true });
        setCategories(res.categories || []);
      } catch (e) {
        console.error('Erreur chargement catégories:', e);
      }
    })();
  }, []);


  let currentAbortController;
  const loadProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Annuler la requête précédente si elle est en cours
      if (currentAbortController) {
        currentAbortController.abort();
      }
      currentAbortController = new AbortController();
      const { signal } = currentAbortController;

      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      };

      // Nettoyer les filtres vides
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await productService.getProducts(params, { signal });

      setProducts(response.products);
      setPagination(response.pagination);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.message === 'canceled') {
        // requête annulée, ne pas afficher d'erreur
        return;
      }
      
      console.error('❌ Erreur lors du chargement des produits:', error);
      console.error('❌ Détails de l\'erreur:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method
        }
      });
      
      let errorMessage = 'Erreur lors du chargement des produits';
      if (error.response) {
        if (error.response.status === 429) {
          errorMessage = 'Trop de requêtes. Veuillez attendre quelques minutes.';
        } else if (error.response.status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.response.status === 403) {
          errorMessage = 'Accès refusé. Vérifiez vos permissions.';
        } else if (error.response.status === 500) {
          errorMessage = 'Erreur serveur. Contactez l\'administrateur.';
        } else {
          errorMessage = `Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = 'Aucune réponse du serveur. Vérifiez la connexion.';
      } else {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    // Mettre à jour l'URL pour le paramètre de recherche
    if (key === 'search') {
      const newSearchParams = new URLSearchParams(searchParams);
      if (value) {
        newSearchParams.set('search', value);
      } else {
        newSearchParams.delete('search');
      }
      setSearchParams(newSearchParams);
    }
  };

  const handleDelete = async (productId, productName) => {
    // Confirmation avant suppression
    const confirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer le produit "${productName}" ?`);
    if (!confirmed) return;

    try {
      console.log('🔄 Suppression du produit:', productId);
      const response = await productService.deleteProduct(productId);
      console.log('✅ Produit supprimé avec succès:', response);
      
      // Mettre à jour la liste des produits
      setProducts(products.filter(p => p.id !== productId));
      
      // Afficher un message de succès
      message.success('Produit supprimé avec succès');
      
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      
      let errorMessage = 'Erreur lors de la suppression du produit';
      if (error.response) {
        errorMessage = `Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Aucune réponse du serveur';
      } else {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    }
  };

  const toggleVisibility = async (product) => {
    try {
      const updated = await productService.toggleVisibility(product.id);
      setProducts(products.map(p => p.id === product.id ? { ...p, isVisible: updated.product.isVisible, status: updated.product.status } : p));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de visibilité:', error);
      alert('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleViewProduct = (productId) => {
    setSelectedProductId(productId);
    setIsModalVisible(true);
  };


  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedProductId(null);
  };

  const handleTopExport = async (format, scope) => {
    try {
      const { exportService } = await import('../services/exportService');
      if (scope === 'all') {
        if (format === 'excel') {
          await exportService.exportProductsToExcel(filters);
        } else if (format === 'pdf') {
          await exportService.exportProductsToPDF(filters);
        }
      } else if (scope === 'selected') {
        if (selectedRowKeys.length === 0) {
          alert('Sélectionnez au moins un produit.');
          return;
        }
        if (selectedRowKeys.length === 1) {
          const productId = selectedRowKeys[0];
          if (format === 'excel') {
            await exportService.exportSingleProductToExcel(productId);
          } else if (format === 'pdf') {
            await exportService.exportSingleProductToPDF(productId);
          }
        } else {
          const enhancedFilters = { ...filters, selectedIds: selectedRowKeys };
          if (format === 'excel') {
            await exportService.exportProductsToExcel(enhancedFilters);
          } else if (format === 'pdf') {
            await exportService.exportProductsToPDF(enhancedFilters);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Brouillon', class: 'badge--warning' },
      published: { label: 'Publié', class: 'badge--success' },
      archived: { label: 'Archivé', class: 'badge--secondary' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'badge--secondary' };
    
    return (
      <span className={`badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="products-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement des produits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>Gestion des Produits</Title>
        <Space>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'all-excel',
                  label: `Exporter tous (${pagination?.totalItems ? ` ${pagination.totalItems}` : ''})`,
                  icon: <i className="fas fa-file-excel"></i>,
                  onClick: () => handleTopExport('excel', 'all')
                },
                { type: 'divider' },
                {
                  key: 'selected-excel',
                  label: `Exporter la sélection (${` ${selectedRowKeys.length}`  })`,
                  icon: <i className="fas fa-file-excel"></i>,
                  disabled: selectedRowKeys.length === 0,
                  onClick: () => handleTopExport('excel', 'selected')
                }
                
              ]
              
            }}
            trigger={['click']}
          >
            
            <Button size="large" className="template-button--info" title="Exporter Produits" style={{ backgroundColor: 'rgb(84, 186, 102)', color: '#fff', padding: '8px 20px', fontSize: 16, height: 44 }}>
              {`Exporter Produits`}
            </Button>
          </Dropdown>
          <TemplateButton 
            label="Template Import"
            icon="fas fa-download"
            className="template-button--info"
            onSuccess={(message) => {
              console.log('Template téléchargé:', message);
            }}
            onError={(error) => {
              alert(`Erreur téléchargement template: ${error}`);
            }}
          />
          <ImportButton
            type="products"
            className="import-button--primary"
            icon="fas fa-file-import"
            onSuccess={(result) => {
              console.log('Import réussi:', result);
              setFilters({});
              setPagination(prev => ({ ...prev, currentPage: 1 }));
              loadProducts();
            }}
            onError={(error) => {
              alert(`Erreur d'import: ${error}`);
            }}
          />
          <Button type="primary" style={{backgroundColor:'#002c3e'}}>
            <Link to="/admin/products/new"><i className="fas fa-plus"></i> Nouveau produit</Link>
          </Button>
        </Space>
      </div>

      {error && (
        <div className="alert alert--danger">
          <i className="fas fa-exclamation-triangle alert__icon"></i>
          <div className="alert__content">
            <div className="alert__title">Erreur</div>
            <div className="alert__message">{error}</div>
          </div>
        </div>
      )}


      <Card style={{ marginBottom: 16 }}>
        {/* Aide Google Drive */}
        <GoogleDriveHelp />
        
        <Row gutter={[12, 12]}>
          <Col xs={24} md={6}>
            <Text strong>Rechercher</Text>
            <Input 
              placeholder="Nom, référence..." 
              value={query} 
              onChange={(e) => {
                setQuery(e.target.value);
                handleFilterChange('search', e.target.value);
              }} 
            />
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Statut</Text>
            <Select
              value={filters.status || ''}
              onChange={(v) => handleFilterChange('status', v)}
              style={{ width: '100%' }}
              options={[
                { value: '', label: 'Tous les statuts' },
                { value: 'draft', label: 'Brouillon' },
                { value: 'published', label: 'Publié' },
                { value: 'archived', label: 'Archivé' }
              ]}
            />
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Visibilité</Text>
            <Select
              value={filters.isVisible || ''}
              onChange={(v) => handleFilterChange('isVisible', v)}
              style={{ width: '100%' }}
              options={[
                { value: '', label: 'Tous' },
                { value: 'true', label: 'Visible' },
                { value: 'false', label: 'Masqué' }
              ]}
            />
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Catégorie</Text>
            <Select
              value={filters.category || ''}
              onChange={(v) => handleFilterChange('category', v)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              options={[{ value: '', label: 'Toutes les catégories' }, ...categories.map(c => ({ value: c.id, label: c.name }))]}
            />
          </Col>
        </Row>
      </Card>

      <div className="products-section">
        <div className="section-header">
          <h2>
            Produits ({pagination.totalItems})
          </h2>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -10 }}>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'selected',
                      label: `Supprimer la sélection (${selectedRowKeys.length})`,
                      disabled: selectedRowKeys.length === 0,
                      onClick: async () => {
                        Modal.confirm({
                          title: `Supprimer ${selectedRowKeys.length} produit(s) sélectionné(s) ?`,
                          okText: 'Oui',
                          cancelText: 'Non',
                          okButtonProps: { danger: true },
                          onOk: async () => {
                            try {
                              if (selectedRowKeys.length === 0) return;
                              await productService.bulkDeleteProducts(selectedRowKeys);
                              setSelectedRowKeys([]);
                              loadProducts();
                            } catch (e) {
                              Modal.error({ title: 'Erreur', content: 'Erreur suppression en masse' });
                            }
                          }
                        });
                      }
                    },
                    {
                      type: 'divider'
                    },
                    {
                      key: 'all',
                      danger: true,
                      label: 'Supprimer TOUT le tableau',
                      onClick: async () => {
                        Modal.confirm({
                          title: 'Supprimer TOUT le tableau de produits ?',
                          content: 'Cette action est irréversible et supprimera tous les produits de la base.',
                          okText: 'Oui, supprimer tout',
                          cancelText: 'Annuler',
                          okButtonProps: { danger: true },
                          onOk: async () => {
                            try {
                              console.log('🔄 Suppression totale de tous les produits...');
                              const response = await productService.bulkDeleteAllProducts();
                              console.log('✅ Suppression totale réussie:', response);
                              
                              setSelectedRowKeys([]);
                              loadProducts();
                              
                              message.success(`Suppression totale réussie: ${response.deleted || 0} produits supprimés`);
                            } catch (error) {
                              console.error('❌ Erreur suppression totale:', error);
                              let errorMessage = 'Erreur suppression totale';
                              if (error.response) {
                                errorMessage = `Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
                              } else if (error.request) {
                                errorMessage = 'Aucune réponse du serveur';
                              } else {
                                errorMessage = error.message;
                              }
                              
                              Modal.error({ 
                                title: 'Erreur suppression totale',
                                content: errorMessage,
                                width: 600
                              });
                            }
                          }
                        });
                      }
                    },
                  ]
                }}
              >
                <Button danger type="primary">
                  <i className="fas fa-trash"></i> Supprimer
                </Button>
              </Dropdown>
            </div>
        </div>

        {products.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-box"></i>
            <h3>Aucun produit trouvé</h3>   
          </div>
        ) : (
          <>
            <Table
              rowKey="id"
              dataSource={products}
              pagination={false}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
              }}
              columns={[
                {
                  title: 'Image',
                  dataIndex: ['images', 'main'],
                  render: (value, product) => {
                    return (
                      <SmartImage
                        src={getProductMainImage(product)}
                        alt={product.name || 'Produit'}
                        width={60}
                        height={60}
                        imgStyle={{ objectFit: 'cover' }}
                      />
                    );
                  }
                },
                {
                  title: 'Produit',
                  dataIndex: 'name',
                  render: (text, product) => (
                    <div className="product-info">
                      <strong>{product.name}</strong>
                      <div>{product.shortDescription || 'Aucune description'}</div>
                    </div>
                  )
                },
                { title: 'Référence', dataIndex: 'reference', render: (t) => <code>{t}</code> },
                {
                  title: 'Catégories',
                  dataIndex: 'categories',
                  render: (cats = []) => (
                    <Space size={[4, 4]} wrap>
                      {cats.slice(0, 2).map((c) => <Tag key={c.id}>{c.name}</Tag>)}
                      {cats.length > 2 && <Tag>+{cats.length - 2}</Tag>}
                    </Space>
                  )
                },
                {
                  title: 'Statut',
                  dataIndex: 'status',
                  render: (status) => (
                    <Tag color={status === 'published' ? 'green' : status === 'draft' ? 'orange' : 'default'}>
                      {status === 'published' ? 'Publié' : status === 'draft' ? 'Brouillon' : 'Archivé'}
                    </Tag>
                  )
                },
                {
                  title: 'Visibilité',
                  dataIndex: 'isVisible',
                  render: (v, product) => (
                    <Switch checked={product.isVisible} onChange={() => toggleVisibility(product)} />
                  )
                },
                { title: 'Date', dataIndex: 'createdAt', render: (d) => <span className="date-text">{formatDate(d)}</span> },
                {
                  title: 'Actions',
                  render: (_, product) => (
                    <Space size={8}>
                      <Tooltip title="Voir les détails">
                        <Button 
                          type="default"
                          shape="circle"
                          icon={<i className="fas fa-eye"></i>} 
                          onClick={() => handleViewProduct(product.id)}
                        />
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <Link to={`/admin/products/${product.id}/edit`}>
                          <Button type="primary" shape="circle" icon={<i className="fas fa-edit"></i>} />
                        </Link>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <Button danger shape="circle" icon={<i className="fas fa-trash"></i>} onClick={() => handleDelete(product.id, product.name)} />
                      </Tooltip>
                    </Space>
                  )
                },
              ]}
            />



            {/* Pagination personnalisée */}
            {pagination.totalPages > 1 && (
              <CustomPagination
                current={pagination.currentPage}
                total={pagination.totalItems}
                pageSize={pagination.itemsPerPage}
                onChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
                showSizeChanger={true}
                showQuickJumper={true}
                showTotal={true}
                size="default"
              />
            )}
          </>
        )}
      </div>

      {/* Modal de détails du produit */}
      <ProductDetailsModal
        visible={isModalVisible}
        onClose={handleCloseModal}
        productId={selectedProductId}
      />
    </div>
  );
};

export default Products;
